import React, { useEffect, useRef, useMemo, useCallback } from "react";
import ChatInput from "./ChatInput";

const ChatPane = React.memo(({
    messages,
    loading,
    error,
    errorStatus,
    onResend,
    onSend,
    onStop,
    requestTimerSeconds,
    onExpandFullScreen,
    onDownload,
    onToggleStats,
    isSubChat,
    isStartChatFinished,
    onBackToWelcome,
    t,
    passCash,
    model,
    isExpanded,
    onToggleExpand,
    showChatStats,
    isRegeneratingQuestions
}) => {
    const messagesEndRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    const cleanBotHtml = useCallback((html) => {
        if (!html) return "";
        const match = html.match(/<script\s+id=['"]lesson-questions-data['"]/i);
        if (match && match.index !== undefined) {
            return html.substring(0, match.index).trim();
        }
        return html.replace(/<script\s+id=['"]lesson-questions-data['"]\s+type=['"]application\/json['"]>[\s\S]*?<\/script>/gi, "").trim();
    }, []);

    const getTextStats = useCallback((html) => {
        if (!html) return { words: 0, characters: 0, readingTime: 0 };
        const text = html.replace(/<[^>]*>?/gm, '').trim();
        const words = text.split(/\s+/).filter(w => w.length > 0).length;
        const characters = text.replace(/\s+/g, '').length;
        const readingTime = Math.max(1, Math.ceil(words / 180));
        return { words, characters, readingTime };
    }, []);

    const isLessonMessage = useCallback((msg) => {
        if (!msg) return false;
        if (msg.isLesson !== undefined) {
            return msg.isLesson;
        }
        if (!msg.text) return false;
        return msg.text.includes("lesson-questions-data");
    }, []);

    const displayedMessages = useMemo(() => {
        if (!isSubChat) return messages;

        const firstLessonIndex = messages.findIndex(m => m.sender === "bot" && (isLessonMessage(m) || m.isLesson === true));
        if (firstLessonIndex !== -1) {
            return messages.filter((m, idx) => {
                if (idx === firstLessonIndex) return false;
                if (idx === firstLessonIndex - 1 && messages[idx].sender === "user") return false;
                if (m.isEvaluation || (m.text && m.text.includes("لقد أكملت حل الأسئلة التفاعلية للدرس"))) return false;
                return true;
            });
        } else {
            let firstUserIndex = messages.findIndex(msg => msg.sender === "user");
            let firstBotIndex = messages.findIndex(msg => msg.sender === "bot");
            return messages.filter((msg, idx) => {
                if (idx === firstUserIndex) return false;
                if (idx === firstBotIndex) return false;
                if (msg.isEvaluation || (msg.text && msg.text.includes("لقد أكملت حل الأسئلة التفاعلية للدرس"))) return false;
                return true;
            });
        }
    }, [messages, isSubChat, isLessonMessage]);

    useEffect(() => {
        scrollToBottom();
    }, [displayedMessages, loading, scrollToBottom]);

    return (
        <div className={`chat-pane-container h-100 d-flex flex-column ${isSubChat ? "sub-chat-pane" : ""}`}>
            <div className="chat-header d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
                <div className="chat-header-info">
                    <div className="chat-header-text-group text-right">
                        <h2 className="chat-header-title h6 font-weight-bold m-0 d-flex align-items-center">
                            <i className="fal fa-robot text-primary ml-2"></i>
                            {t("Pages.Chatbot.ChatPaneTitle")}
                        </h2>
                    </div>
                </div>
                {isSubChat && (
                    <div className="chat-header-actions">
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-secondary p-1"
                            onClick={onToggleExpand}
                            title={isExpanded ? t("Pages.Chatbot.CollapseChat") : t("Pages.Chatbot.ExpandChat")}
                            style={{ width: 'auto' }}
                        >
                            <i className={`fal ${isExpanded ? "fa-compress-arrows-alt" : "fa-arrows-alt-h"}`} style={{ fontSize: '16px' }}></i>
                        </button>
                    </div>
                )}
            </div>

            <div className="chat-messages-area">
                {displayedMessages.length === 0 ? (
                    <div className="empty-chat-state">
                        <div className="empty-chat-icon-container">
                            <i className="fal fa-comments empty-chat-icon"></i>
                        </div>
                        <div>
                            <h4>{t("Pages.Chatbot.ChatPaneTitle")}</h4>
                            <p className="mt-2">{t("Pages.Chatbot.SubChatHint")}</p>
                        </div>
                    </div>
                ) : (
                    displayedMessages.map((msg, index) => {
                        const origIndex = messages.indexOf(msg);
                        const isBot = msg.sender === "bot";
                        const totalTimeMs = (msg.databaseTimeMs || 0) + (msg.apiTimeMs || 0);
                        const totalTimeSec = (totalTimeMs / 1000).toFixed(2);
                        return (
                            <div
                                key={index}
                                className={`message-row ${msg.sender} animate__animated animate__fadeInUp animate__faster`}
                            >
                                <div className="message-bubble-wrapper">
                                    <div className={`message-bubble ${msg.sender}`}>
                                        {isBot ? (
                                            (msg.text === "" && msg.isStreaming) ? (
                                                <div className="typing-indicator-container">
                                                    <div className="typing-indicator">
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                    <div className="waiting-response-message">
                                                        <span>{msg.statusText || t("Pages.Chatbot.WaitingResponse") || "جاري انتظار رد المساعد..."}</span>
                                                        <span className="waiting-timer">({requestTimerSeconds} {t("Pages.Chatbot.Sec")})</span>
                                                    </div>
                                                </div>
                                            ) : msg.text && msg.text.includes("[NEW_CHAT_REQUIRED]") ? (
                                                <div className="alert-card new-chat-required-card" style={{ padding: '15px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#b91c1c', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                                                        <i className="fal fa-exclamation-circle" style={{ fontSize: '18px' }}></i>
                                                        <span>{t("Pages.Chatbot.NewChatRequiredError")}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={onBackToWelcome}
                                                        style={{ border: 'none', background: 'none', color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '14px', fontWeight: '600', textAlign: 'right' }}
                                                    >
                                                        {t("Pages.Chatbot.OpenNewChatLink")}
                                                    </button>
                                                </div>
                                            ) : msg.text && msg.text.includes("CHAT_UNSPOURTED") ? (
                                                <div className="alert-card chat-unsupported-card" style={{ padding: '15px', borderRadius: '12px', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', color: '#b45309', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                                                    <i className="fal fa-globe" style={{ fontSize: '18px' }}></i>
                                                    <span>{t("Pages.Chatbot.ChatUnsupportedError")}</span>
                                                </div>
                                            ) : isLessonMessage(msg) ? (
                                                msg.isStreaming ? (
                                                    <div className="typing-indicator-container py-2">
                                                        <i className="fal fa-spinner fa-spin ml-2 text-primary" style={{ fontSize: '20px', color: '#92278f' }}></i>
                                                        <div className="waiting-response-message">
                                                            <span>{t("Pages.Chatbot.GeneratingLesson")}</span>
                                                            <span className="waiting-timer">({requestTimerSeconds} {t("Pages.Chatbot.Sec")})</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="generated-success-message">
                                                        <div className="success-icon-wrapper">
                                                            <i className="fal fa-sparkles"></i>
                                                        </div>
                                                        <div className="success-text-content">
                                                            <h5>{t("Pages.Chatbot.LessonGeneratedTitle")}</h5>
                                                            <p>{t("Pages.Chatbot.LessonGeneratedDesc")}</p>
                                                        </div>
                                                    </div>
                                                )
                                            ) : msg.isStreaming ? (
                                                <>
                                                    <div
                                                        className="bot-answer-html bot-answer-streaming"
                                                        dangerouslySetInnerHTML={{ __html: cleanBotHtml(msg.text) }}
                                                    />
                                                    <div className="streaming-status-row d-flex align-items-center gap-2 mt-2 pt-2 border-top" style={{ fontSize: '12px', color: '#64748b' }}>
                                                        <i className="fal fa-spinner fa-spin text-primary"></i>
                                                        <span>{msg.statusText || t("Pages.Chatbot.StreamingAnswer") || "جاري كتابة الإجابة..."}</span>
                                                        <span className="waiting-timer">({requestTimerSeconds} {t("Pages.Chatbot.Sec")})</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div
                                                        className="bot-answer-html"
                                                        dangerouslySetInnerHTML={{ __html: cleanBotHtml(msg.text) }}
                                                    />
                                                    {msg.text && (
                                                        <div className="bot-text-info-bar">
                                                            <span>
                                                                <i className="fal fa-file-alt"></i>
                                                                <span>{t("Pages.Chatbot.Words")} {getTextStats(cleanBotHtml(msg.text)).words}</span>
                                                            </span>
                                                            <span>
                                                                <i className="fal fa-font"></i>
                                                                <span>{t("Pages.Chatbot.Characters")} {getTextStats(cleanBotHtml(msg.text)).characters}</span>
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        ) : (
                                            <div className="user-message-text">{msg.text}</div>
                                        )}
                                    </div>
                                    {isBot && !msg.isStreaming && msg.text !== "" && (
                                        <div className="bot-bubble-actions-row">
                                            {showChatStats && totalTimeMs > 0 && (
                                                <div
                                                    className="stats-badge total-time-badge"
                                                    onClick={() => onToggleStats(origIndex)}
                                                    role="button"
                                                    tabIndex="0"
                                                    title={t("Pages.Chatbot.TotalTime")}
                                                    style={{ gap: "8px" }}
                                                >
                                                    <i className="fal fa-robot"></i>
                                                    <span style={{ textTransform: "capitalize", fontWeight: "bold" }}>
                                                        {msg.model || msg.Model || "deepseek"}
                                                    </span>
                                                    <span style={{ opacity: 0.3 }}>|</span>
                                                    <i className="fal fa-clock"></i>
                                                    <span>
                                                        {t("Pages.Chatbot.TotalTime")}: {totalTimeSec} {t("Pages.Chatbot.Sec")}
                                                    </span>
                                                </div>
                                            )}
                                            {!isSubChat && (
                                                <div className="bot-bubble-actions">
                                                    <button
                                                        type="button"
                                                        className="fullscreen-expand-btn"
                                                        onClick={() => onExpandFullScreen(
                                                            cleanBotHtml(msg.text),
                                                            messages[origIndex - 1] ? messages[origIndex - 1].text : ""
                                                        )}
                                                        title={t("Pages.Chatbot.FullScreen")}
                                                    >
                                                        <i className="fal fa-expand-arrows"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="bubble-download-btn"
                                                        onClick={() => onDownload(
                                                            cleanBotHtml(msg.text),
                                                            messages[origIndex - 1] ? messages[origIndex - 1].text : ""
                                                        )}
                                                        title={t("Pages.Chatbot.Download")}
                                                    >
                                                        <i className="fal fa-download"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area border-top p-3 bg-white">
                {error && (
                    <div className={`error-panel alert alert-danger d-flex align-items-center p-3 mb-3 gap-3 ${errorStatus || ""}`} role="alert">
                        <i className={
                            errorStatus === "api_error"
                                ? "fal fa-robot animate__animated animate__shakeX ml-2"
                                : errorStatus === "server_error"
                                    ? "fal fa-server animate__animated animate__shakeX ml-2"
                                    : errorStatus === "not_found"
                                        ? "fal fa-exclamation-circle animate__animated animate__shakeX ml-2"
                                        : "fal fa-wifi-slash animate__animated animate__flash animate__infinite ml-2"
                        }></i>
                        <div style={{ flex: 1, textAlign: 'right' }}>
                            <strong>
                                {errorStatus === "api_error"
                                    ? t("Pages.Chatbot.AiError")
                                    : errorStatus === "server_error"
                                        ? t("Pages.Chatbot.ServerError")
                                        : errorStatus === "not_found"
                                            ? t("Pages.Chatbot.NotFoundError")
                                            : t("Pages.Chatbot.ConnectionError")}
                            </strong>
                            <span className="d-block mt-1 small">{errorStatus !== "not_found" && error}</span>
                        </div>
                        {errorStatus !== "not_found" && (
                            <button
                                type="button"
                                className="chatbot-btn btn btn-danger btn-sm ml-2"
                                onClick={onResend}
                                style={{ alignSelf: "center" }}
                            >
                                <i className="fal fa-redo"></i>
                                <span>{t("Pages.Chatbot.Resend")}</span>
                            </button>
                        )}
                    </div>
                )}
                <ChatInput
                    onSend={onSend}
                    onStop={onStop}
                    loading={loading || !isStartChatFinished}
                    t={t}
                    isSubChat={isSubChat}
                    passCash={passCash}
                    model={model}
                    isRegeneratingQuestions={isRegeneratingQuestions}
                />
            </div>
        </div>
    );
});

export default ChatPane;
